import { ClubData, ClubOption, PAGE, SingleOption, SingleValueOption } from './typeInterfaces';
import { OPTIONSENUM } from './filtersOptions';
import { ACTUALBUDGET } from './typeInterfaces';
import { AgeGroup, EmploymentStatus, PositionAchieved, RegisteredPosition, Staff, StaffRoles } from '../../../api/typesDc';
import { clubsData, LanguageCode } from '../../../api/types';
import { fetchClubIdsByStaticData, getClubOptions } from './getLicenseAnalyticsActions';
import { useSelector } from 'react-redux';
import { store } from '../../../redux/store';

const state:any = store.getState();
const isAdmin = state.common.isAdmin;
const activeClubId = state.clubDetails.id ? state.clubDetails.id : undefined;
// Calculation
const type = {
  TOTAL: 'Total',
  INCOME: 'Income',
  EXPENSE: 'Expense',
};
const subType = {
  I: 'I',
  O: 'O',
  T: 'T',
  F: 'F',
  E: 'E',
  M: 'M',
  L: 'L',
  J: 'J',
};

export function formattedNumber(number:any) {
  return number.toLocaleString('en-US');
}

export function shortenNumber(number:any) {
  let suffixes: string[] = [];

  if (localStorage.getItem('lang') === LanguageCode.Japanese) {
    suffixes = ['', 'K', '百万'];
  } else {
    suffixes = ['', 'K', 'M'];
  }
  let i = 0;

  let positiveNumber = Math.abs(number); // Store the absolute value

  while (positiveNumber >= 1000 && i < suffixes.length - 1) {
    positiveNumber /= 1000;
    i++;
  }

  let suffix = suffixes[i];
  // Format the number with the appropriate suffix
  let formattedNumber = `${positiveNumber.toFixed(1)}${suffix}`;
  formattedNumber = formattedNumber == '0.0' ? '0' : formattedNumber;

  // Add the negative sign back if the original number was negative
  return number < 0 ? `-${formattedNumber}` : formattedNumber;
}

export const getIncome = (dataList: ClubData[]): ClubData[] => {
  return dataList?.filter(item => item.type == type.TOTAL && item.name == type.INCOME);
};
export const getIncomeTotal = (dataList: ClubData[], actualOrBudget: ACTUALBUDGET): number => {
  const income = getIncome(dataList);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (income?.length == 0) {
    return 0;
  }
  if (actualOrBudget === ACTUALBUDGET.ACTUAL) {
    return income?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / distinctClubCount;
  } else {
    return income?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / distinctClubCount;
  }
};
export const getSpend = (dataList: ClubData[]): ClubData[] => {
  return dataList?.filter(item => item.type == type.TOTAL && item.name == type.EXPENSE);
};
export const getSpendTotal = (dataList: any, actualOrBudget: ACTUALBUDGET): number => {
  const spend = getSpend(dataList);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;

  if (spend?.length == 0) {
    return 0;
  }
  if (actualOrBudget === ACTUALBUDGET.ACTUAL) {
    return spend?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / distinctClubCount;
  } else {
    return spend?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / distinctClubCount;
  }
};

export const getYOYTotalSpend = (dataList: ClubData[], actualOrBudget: ACTUALBUDGET): number => {
  // Year over Year Growth (YoY) = (Current Period Value ÷ Prior Period Value) – 1
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const currentYOYList = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year == currentYear.toString(),
  );
  const previousYOYList = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year === previousYear.toString(),
  );
  if (previousYOYList?.length > 0 && currentYOYList.length > 0) {
    if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
      const currentYOY =
        currentYOYList?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / currentYOYList?.length;
      const previousYOY =
        previousYOYList?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / previousYOYList?.length;
      const YOYValue = currentYOY / previousYOY - 1;
      return YOYValue;
    } else {
      const currentYOY =
        currentYOYList?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / currentYOYList?.length;
      const previousYOY =
        previousYOYList?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / previousYOYList?.length;
      const YOYValue = currentYOY / previousYOY - 1;
      return YOYValue;
    }
  }
  return 0;
};
export const getYOYClubSpend = (dataList: ClubData[], actualOrBudget: ACTUALBUDGET): number => {
  // Year over Year Growth (YoY) = (Current Period Value ÷ Prior Period Value) – 1
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const academySpendCY = dataList?.filter(item => item.type === type.EXPENSE && item.year == currentYear.toString());
  const clubSpendTotalCY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year == currentYear.toString(),
  );
  const academySpendPY = dataList?.filter(item => item.type === type.EXPENSE && item.year == previousYear.toString());
  const clubSpendTotalPY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year == previousYear.toString(),
  );

  if (
    academySpendCY.length > 0 &&
    clubSpendTotalCY.length > 0 &&
    clubSpendTotalPY.length > 0 &&
    academySpendPY.length > 0
  ) {
    if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
      const academySpendCYVal =
        academySpendCY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academySpendCY?.length;
      const clubSpendTotalCYVal =
        clubSpendTotalCY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / clubSpendTotalCY?.length;
      const clubSpendOnAcadCY = academySpendCYVal / clubSpendTotalCYVal;

      const academySpendPYVal =
        academySpendPY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academySpendPY?.length;
      const clubSpendTotalPYVal =
        clubSpendTotalPY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / clubSpendTotalPY?.length;
      const clubSpendOnAcadPY = academySpendPYVal / clubSpendTotalPYVal;

      const YOYValue = clubSpendOnAcadCY / clubSpendOnAcadPY - 1;
      return YOYValue;
    } else {
      const academySpendCYVal =
        academySpendCY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academySpendCY?.length;
      const clubSpendTotalCYVal =
        clubSpendTotalCY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / clubSpendTotalCY?.length;
      const clubSpendOnAcadCY = academySpendCYVal / clubSpendTotalCYVal;

      const academySpendPYVal =
        academySpendPY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academySpendPY?.length;
      const clubSpendTotalPYVal =
        clubSpendTotalPY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / clubSpendTotalPY?.length;
      const clubSpendOnAcadPY = academySpendPYVal / clubSpendTotalPYVal;

      const YOYValue = clubSpendOnAcadCY / clubSpendOnAcadPY - 1;
      return YOYValue;
    }
  }
  return 0;
};
export const getYOYAcadProfit = (dataList: ClubData[], actualOrBudget: ACTUALBUDGET): number => {
  // Year over Year Growth (YoY) = (Current Period Value ÷ Prior Period Value) – 1
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const academyIncomeCY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.INCOME && item.year == currentYear.toString(),
  );
  const academySpendCY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year === currentYear.toString(),
  );
  const academyIncomePY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.INCOME && item.year == previousYear.toString(),
  );
  const academySpendPY = dataList?.filter(
    item => item.type == type.TOTAL && item.name == type.EXPENSE && item.year === previousYear.toString(),
  );

  if (
    academyIncomeCY.length > 0 &&
    academySpendCY.length > 0 &&
    academyIncomePY.length > 0 &&
    academySpendPY.length > 0
  ) {
    if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
      const academyIncomeCYVal =
        academyIncomeCY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academyIncomeCY?.length;
      const academySpendCYVal =
        academySpendCY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academySpendCY?.length;
      const acadProfitCY = academyIncomeCYVal - academySpendCYVal;

      const academyIncomePYVal =
        academyIncomePY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academyIncomePY?.length;
      const academySpendPYVal =
        academySpendPY?.reduce((accumulator, item) => accumulator + item.currentYear, 0) / academySpendPY?.length;
      const acadProfitPY = academyIncomePYVal - academySpendPYVal;

      const YOYValue = acadProfitCY / acadProfitPY - 1;
      return YOYValue;
    } else {
      const academyIncomeCYVal =
        academyIncomeCY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academyIncomeCY?.length;
      const academySpendCYVal =
        academySpendCY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academySpendCY?.length;
      const acadProfitCY = academyIncomeCYVal - academySpendCYVal;

      const academyIncomePYVal =
        academyIncomePY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academyIncomePY?.length;
      const academySpendPYVal =
        academySpendPY?.reduce((accumulator, item) => accumulator + item.nextYear, 0) / academySpendPY?.length;
      const acadProfitPY = academyIncomePYVal - academySpendPYVal;

      const YOYValue = acadProfitCY / acadProfitPY - 1;
      return YOYValue;
    }
  }
  return 0;
};

export const getAcademySpendTotal = (dataList: any, actualOrBudget: ACTUALBUDGET): number => {
  const academySpend = dataList?.filter((item:any) => item.type === type.EXPENSE);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (academySpend?.length == 0) {
    return 0;
  }
  if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
    const academySpendTotal = academySpend?.reduce((accumulator:any, item:any) => accumulator + item.currentYear, 0);
    return academySpendTotal / distinctClubCount;
  } else {
    const academySpendTotal = academySpend?.reduce((accumulator:any, item:any) => accumulator + item.nextYear, 0);
    return academySpendTotal / distinctClubCount;
  }
};
export const getAcademyIncomeTotal = (dataList: ClubData[], actualOrBudget: ACTUALBUDGET): number => {
  const academyIncome = dataList?.filter(item => item.type === type.INCOME);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (academyIncome?.length == 0) {
    return 0;
  }
  if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
    const academyIncomeTotal = academyIncome?.reduce((accumulator, item) => accumulator + item.currentYear, 0);
    return academyIncomeTotal / distinctClubCount;
  } else {
    const academyIncomeTotal = academyIncome?.reduce((accumulator, item) => accumulator + item.nextYear, 0);
    return academyIncomeTotal / distinctClubCount;
  }
};
export const getAcademyIncomeSub = (dataList: ClubData[], subtype: string, actualOrBudget: ACTUALBUDGET): number => {
  const academyIncome = dataList?.filter(item => item.type === type.INCOME && item.subType === subtype);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (academyIncome?.length == 0) {
    return 0;
  }
  if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
    const academyIncomeTotal = academyIncome?.reduce((accumulator, item) => accumulator + item.currentYear, 0);
    return academyIncomeTotal / distinctClubCount;
  } else {
    const academyIncomeTotal = academyIncome?.reduce((accumulator, item) => accumulator + item.nextYear, 0);
    return academyIncomeTotal / distinctClubCount;
  }
};
export const getAcademySpendSub = (dataList: ClubData[], subtype: string, actualOrBudget: ACTUALBUDGET): number => {
  const academySpend = dataList?.filter(item => item.type === type.EXPENSE && item.subType === subtype);
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (academySpend?.length == 0) {
    return 0;
  }
  if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
    const academySpendTotal = academySpend?.reduce((accumulator, item) => accumulator + item.currentYear, 0);
    return academySpendTotal / distinctClubCount;
  } else {
    const academySpendTotal = academySpend?.reduce((accumulator, item) => accumulator + item.nextYear, 0);
    return academySpendTotal / distinctClubCount;
  }
};
export const getAcademySpendSubChild = (
  dataList: ClubData[],
  subtype: string | undefined,
  ageGroup: string | undefined,
  actualOrBudget: ACTUALBUDGET,
  isAverage: boolean,
): number => {
  const academySpend = dataList?.filter(
    item =>
      item.type === type.EXPENSE &&
      (subtype ? item.subType === subtype : true) &&
      (ageGroup ? item.otherExpenseName.toLowerCase() === ageGroup?.toLowerCase?.() : true),
  );

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(dataList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;
  if (academySpend?.length == 0) {
    return 0;
  }
  if (actualOrBudget == ACTUALBUDGET.ACTUAL) {
    const academySpendTotal = academySpend?.reduce((accumulator, item) => accumulator + item.currentYear, 0);
    return isAverage ? academySpendTotal / distinctClubCount : academySpendTotal;
  } else {
    const academySpendTotal = academySpend?.reduce((accumulator, item) => accumulator + item.nextYear, 0);
    return isAverage ? academySpendTotal / distinctClubCount : academySpendTotal;
  }
};
export const getClubsByLeagues = (leagueValues: SingleOption[], ClubList: any): ClubData[] => {
  const filteredLeagues = ClubList.filter((value:any) =>
    leagueValues.some(filter => filter.id == (value.club?.leagueId || value.club?.league.id)),
  );
  return filteredLeagues;
};
//to maintain league history
export const getClubsByLeaguesTemp = (
  leagueValues: SingleOption[],
  ClubList: ClubData[],
  leaguesHistory: any,
): ClubData[] => {
  const filteredLeagues = ClubList.filter(value =>
    leagueValues.some(filter => filter.id == (value.club?.leagueId || value.club?.league.id)),
  );
  return filteredLeagues;
};
export const getClubsByStars = (starValues: any, ClubList: any): ClubData[] => {
  const filteredLeagues = ClubList.filter((value:any) =>
    starValues.some((filter:any) => filter.value === value.club?.currentRating),
  );
  return filteredLeagues;
};
export const calculateAcademyProfitability = (dataList: any, actualOrBudget: ACTUALBUDGET): number => {
  const academyIncomeTotal = getAcademyIncomeTotal(dataList, actualOrBudget);
  const academySpendTotal = getAcademySpendTotal(dataList, actualOrBudget);
  return academyIncomeTotal - academySpendTotal;
};
// Get club details
export const getClubsById = (
  tab: PAGE,
  clubId: string | number | undefined,
  response: any,
  getClubId?: (data: any) => string,
) => {
  if (clubId === undefined || clubId === null || clubId === 0) {
    return response;
  }
  return response?.filter((data:any) => (data.clubId == clubId || data.club?.id == clubId)) ?? [];
};
export const getClubsByYear = (year: string | number, financeData: ClubData[]): ClubData[] | StaffRoles[] => {
  return financeData && financeData.length > 0 ? financeData?.filter(data => data.year == year) : [];
};
//to maintain league history
export const getClubsByYearTemp = (
  year: string | number,
  financeData: ClubData[],
  filteredClubIds: [] | any,
): ClubData[] | StaffRoles[] => {
  return financeData && financeData.length > 0 && typeof financeData != 'function'
    ? financeData?.filter(data => data.year == year && filteredClubIds?.includes(data.clubId))
    : [];
};
export const getClubsByRating = (rating: string | number, financeData: ClubData[]): ClubData[] => {
  return financeData?.filter(data => data.club?.currentRating == rating) ?? [];
};
export const getLeagueById = (
  leagueId: string | number,
  financeData: ClubData[],
  getLeagueId = (data:any) => data.club?.leagueId,
): ClubData[] => {
  return financeData?.filter(data => getLeagueId(data) == leagueId) ?? [];
};
export const getClubOptionsByLeague = (leagueId: string | number, clubList: ClubOption[]): ClubOption[] => {
  return clubList.filter(club => club?.leagueId == leagueId);
};
export const getClubOptionsByLeagueAll = (filteredClubIds: [] | any, clubList: any): ClubOption[] => {
  return clubList?.filter((item:any) => filteredClubIds?.includes(item.clubId));
};
export const getClubOptionsByLeagueROI = (filteredClubIds: [] | any, clubList: ClubOption[]): ClubOption[] => {
  return clubList?.filter(club => filteredClubIds?.includes(club.id));
};
export const getClubOptionsByRating = (ratingId: string | number, clubList: ClubOption[]): ClubOption[] => {
  return clubList.filter(club => club?.currentRating == ratingId);
};
export const getUniqueClubs = (financeData: ClubData[]): ClubData[] => {
  const uniqueClubs: ClubData[] = [];
  for (let i = 0; i < financeData.length; i++) {
    for (let j = 0; j < uniqueClubs.length; j++) {
      if (uniqueClubs[j].clubId == financeData[i].clubId) {
        break;
      }
      uniqueClubs.push(financeData[i]);
    }
  }
  return uniqueClubs;
};
export const getClubsByFilter = (
  filtervalues: SingleOption[],
  allClubData: any,
  getClub: (obj: any) => ClubOption = (obj): ClubOption => obj,
  getLeague: (obj: any) => string = (club): string => club?.leagueId,
  getRating: (obj: any) => string = (club): string => club?.targetRating,
): ClubOption[] | any => {
  const filteredLeagues = allClubData.filter((value:any) =>
    filtervalues.some(filter => filter.id.toString() == getLeague(getClub(value))),
  );
  if (filteredLeagues?.length > 0) {
    return filteredLeagues;
  }
  return allClubData?.filter((club:any) => {
    const result = filtervalues.some(filter => {
      const result = filter.id == ((getRating(getClub(club)) as unknown) as number);
      return result;
    });
    return result;
  });
};
export const getClubsfromFacility = (clubId: string | number, facilityData: ClubData[]) => {
  return facilityData?.filter(data => data.club?.id == clubId) ?? [];
};
export const getFacilityfromFilter = (filtervalues: SingleOption[], facilityData: any[]): any[] => {
  const filteredLeagues = facilityData.filter(value =>
    filtervalues.some(filter => filter.id.toString() == value.club?.league.id),
  );
  if (filteredLeagues?.length > 0) {
    return filteredLeagues;
  }
  return facilityData?.filter(item => filtervalues.some(filter => filter.id == item.club?.currentRating));
};
// Year Calculation
export const getLastFiveYear = (year: number): string[] => {
  let yearInt: number = new Date().getFullYear();
  const years: string[] = [];
  for (let i = 0; i < 5; i++) {
    years.push(yearInt.toString());
    --yearInt;
  }

  return years.reverse();
};

export const calculateSelectedFilterDataByYear = (
  clubOrLeagueList: ClubData[],
  year: any,
  firstFilter: string,
  subFilter: string,
  actualOrBudget: ACTUALBUDGET,
  isAverage: boolean,
  filteredClubIds: [number],
): number => {
  const clubOrLeagueListInyear = clubOrLeagueList.filter(
    (clubOrLeague: ClubData) =>
      clubOrLeague.year == year && clubOrLeague?.clubId && filteredClubIds?.includes(clubOrLeague?.clubId),
  );
  if (firstFilter == OPTIONSENUM.ACADEMYPROFITABILITY) {
    return calculateAcademyProfitability(clubOrLeagueListInyear, actualOrBudget) ?? 0;
  } else if (firstFilter == OPTIONSENUM.CLUBINCOME) {
    return getIncomeTotal(clubOrLeagueListInyear, actualOrBudget) ?? 0;
  } else if (firstFilter == OPTIONSENUM.ACADEMYINCOME) {
    if (subFilter == OPTIONSENUM.MEMBERSHIPFEEINCOME) {
      return getAcademyIncomeSub(clubOrLeagueListInyear, subType.M, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.JLEAGUESUPPORTFUND) {
      return getAcademyIncomeSub(clubOrLeagueListInyear, subType.J, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.OTHER) {
      return getAcademyIncomeSub(clubOrLeagueListInyear, subType.O, actualOrBudget);
    } else {
      return getAcademyIncomeTotal(clubOrLeagueListInyear, actualOrBudget) ?? 0;
    }
  } else if (firstFilter == OPTIONSENUM.TOTALACADEMYCOSTS) {
    if (subFilter == OPTIONSENUM.LABOURCOST) {
      return getAcademySpendSub(clubOrLeagueListInyear, subType.L, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.FACILITIES) {
      return getAcademySpendSub(clubOrLeagueListInyear, subType.F, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.TRAVEL) {
      return getAcademySpendSub(clubOrLeagueListInyear, subType.T, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.EQUIPMENTFIXTURES) {
      return getAcademySpendSub(clubOrLeagueListInyear, subType.E, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.OTHER) {
      return getAcademySpendSub(clubOrLeagueListInyear, subType.O, actualOrBudget);
    } else if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        undefined,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, undefined, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, undefined, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        undefined,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendTotal(clubOrLeagueListInyear, actualOrBudget);
    }
  } else if (firstFilter == OPTIONSENUM.CLUBSPEND || firstFilter == OPTIONSENUM.CLUBCOSTS) {
    return getSpendTotal(clubOrLeagueListInyear, actualOrBudget) ?? 0;
  } else if (firstFilter == OPTIONSENUM.ACADEMYSPEND) {
    return getAcademyIncomeTotal(clubOrLeagueListInyear, actualOrBudget) ?? 0;
  } else if (firstFilter == OPTIONSENUM.ACADEMYCOSTSCLUBCOSTS) {
    const clubSpend = getSpendTotal(clubOrLeagueListInyear, actualOrBudget);
    if (clubSpend == 0) {
      return 0;
    }
    const academySpend = getAcademySpendTotal(clubOrLeagueListInyear, actualOrBudget);
    const percentageSpent = (academySpend / clubSpend) * 100;
    return percentageSpent ? percentageSpent : 0;
  } else if (firstFilter == OPTIONSENUM.CLUBSPENDONACADEMY) {
    const clubSpend = getSpendTotal(clubOrLeagueListInyear, actualOrBudget);
    const academySpend = getAcademySpendTotal(clubOrLeagueListInyear, actualOrBudget);
    return clubSpend - academySpend;
  } else if (firstFilter == OPTIONSENUM.LABOURCOST) {
    if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.L,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.L, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.L, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.L,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.L, undefined, actualOrBudget, isAverage);
    }
  } else if (firstFilter == OPTIONSENUM.FACILITIES) {
    if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.F,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.F, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.F, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.F,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.F, undefined, actualOrBudget, isAverage);
    }
  } else if (firstFilter == OPTIONSENUM.TRAVEL) {
    if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.T,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.T, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.T, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.T,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.T, undefined, actualOrBudget, isAverage);
    }
  } else if (firstFilter == OPTIONSENUM.EQUIPMENTFIXTURES) {
    if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.E,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.E, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.E, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.E,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.E, undefined, actualOrBudget, isAverage);
    }
  } else if (firstFilter == OPTIONSENUM.OTHER) {
    if (subFilter == OPTIONSENUM.U12DEVELOPMENTCENTRE) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.O,
        OPTIONSENUM.U12DEVELOPMENTCENTRE,
        actualOrBudget,
        isAverage,
      );
    } else if (subFilter == OPTIONSENUM.U15S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.O, OPTIONSENUM.U15S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.U18S) {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.O, OPTIONSENUM.U18S, actualOrBudget, isAverage);
    } else if (subFilter == OPTIONSENUM.SOCCERSCHOOL) {
      return getAcademySpendSubChild(
        clubOrLeagueListInyear,
        subType.O,
        OPTIONSENUM.SOCCERSCHOOL,
        actualOrBudget,
        isAverage,
      );
    } else {
      return getAcademySpendSubChild(clubOrLeagueListInyear, subType.O, undefined, actualOrBudget, isAverage);
    }
  }

  return 0;
};

export const calculateSelectedStaffFilterDataByYear = (
  staffList: any[],
  year: any,
  firstFilter: string,
  subFilter: string,
  isAverage: boolean,
  filteredClubIds: [],
): number => {
  let subFilteredStaff: any[] = [];

  staffList = getClubsByYearTemp(year, staffList, filteredClubIds);

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(staffList?.map(item => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size;

  if (subFilter == OPTIONSENUM.FULLTIME) {
    subFilteredStaff = staffList.filter(value => {
      return value?.employmentStatus == EmploymentStatus.ES_FULL_TIME;
    });
  } else if (subFilter == OPTIONSENUM.PARTTIME) {
    subFilteredStaff = staffList.filter(value => {
      return value?.employmentStatus == EmploymentStatus.ES_PART_TIME;
    });
  } else if (subFilter == OPTIONSENUM.PROFESSIONALCONTRACT) {
    subFilteredStaff = staffList.filter(value => {
      return value?.employmentStatus == EmploymentStatus.ES_CONSULTANT;
    });
  } else if (subFilter == OPTIONSENUM.STUDENT) {
    subFilteredStaff = staffList.filter(value => {
      return value?.employmentStatus == EmploymentStatus.ES_STUDENT;
    });
  } else if (subFilter == OPTIONSENUM.OTHEREMPLOYMENTSTATUS) {
    subFilteredStaff = staffList.filter(value => {
      return value?.employmentStatus == EmploymentStatus.ES_VOLUNTEER;
    });
  } else if (subFilter == OPTIONSENUM.COACHESSAQUALIFIED) {
    const qualifiedCoachCount = staffList.filter(
      staff =>
        staff &&
        staff.qualifications != '' &&
        staff.qualifications?.length > 0 && // Check if staff is not null
        !(
          staff.qualifications?.length === 1 &&
          (staff.qualifications[0] === 'DC_Q_QUALIFICATION28' || staff.qualifications[0] === 'DC_Q_QUALIFICATION29')
        ) &&
        !(
          staff.qualifications?.length === 2 &&
          staff.qualifications.includes('DC_Q_QUALIFICATION28') &&
          staff.qualifications.includes('DC_Q_QUALIFICATION29')
        ),
    );
    return staffList.length ? (qualifiedCoachCount.length / staffList.length) * 100 : 0;
  } else if (subFilter == OPTIONSENUM.TOPTEAMDIRECTOR) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.DC_RP_HEAD_COACH);
    });
  } else if (subFilter == OPTIONSENUM.TOPTEAMCOACH) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.DC_RP_HEAD_COACH);
    });
  } else if (subFilter == OPTIONSENUM.ACADEMICDIRECTOR) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.RP_ACADEMY_DIRECTOR);
    });
  } else if (subFilter == OPTIONSENUM.ACADEMICTEAMDIRECTOR) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.RP_ACADEMIC_TEAM_DIRECTOR);
    });
  } else if (subFilter == OPTIONSENUM.ACADEMICTEAMCOACH) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.RP_ACADEMIC_TEAM_COACH);
    });
  } else if (subFilter == OPTIONSENUM.SCHOOLCOACH) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.RP_SCHOOL_COACH);
    });
  } else if (subFilter == OPTIONSENUM.OTHER) {
    subFilteredStaff = staffList.filter(value => {
      return value?.registeredPosition?.includes?.(RegisteredPosition.RP_OTHER);
    });
  } else if (!Number.isNaN(parseInt(subFilter))) {
    subFilteredStaff = staffList.filter(value => {
      return value?.department?.includes?.(subFilter);
    });
  } else {
    subFilteredStaff = staffList;
  }

  // Do staff calculations
  if (firstFilter == OPTIONSENUM.TOTALSTAFF) {
    return calculateResult(subFilteredStaff?.length, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.STAFFCOSTS) {
    let totalCost = 0;
    for (const staff of subFilteredStaff) {
      totalCost += staff.annualSalary;
    }
    return calculateResult(totalCost, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.QUALIFIEDCOACHES) {
    if (subFilter?.length > 0) {
      const staffs = subFilteredStaff.filter(staff => staff.qualifications?.indexOf(subFilter) >= 0);
      return calculateResult(staffs.length, distinctClubCount, isAverage);
    } else {
      const qualifiedCoachCount = staffList.filter(
        staff =>
          staff &&
          staff.qualifications != '' &&
          staff.qualifications?.length > 0 && // Check if staff is not null
          !(
            staff.qualifications?.length === 1 &&
            (staff.qualifications[0] === 'DC_Q_QUALIFICATION28' || staff.qualifications[0] === 'DC_Q_QUALIFICATION29')
          ) &&
          !(
            staff.qualifications?.length === 2 &&
            staff.qualifications.includes('DC_Q_QUALIFICATION28') &&
            staff.qualifications.includes('DC_Q_QUALIFICATION29')
          ),
      );
      return calculateResult(qualifiedCoachCount.length, distinctClubCount, isAverage);
    }
  } else if (firstFilter == OPTIONSENUM.AVERAGESALARY) {
    let totalCost = 0;
    for (const staff of subFilteredStaff) {
      totalCost += staff.annualSalary;
    }
    // Only consider the staffs who has annual salary greater than zero
    subFilteredStaff = subFilteredStaff.filter(staff => staff.annualSalary != 0);
    return subFilteredStaff.length > 0 ? totalCost / subFilteredStaff.length : 0;
  }
  return 0;
};

function calculateResult(subFilteredStaff:any, distinctClubCount:any, isAverage:any) {
  if (distinctClubCount === 0) {
    // Handle case where distinctClubCount is zero
    return subFilteredStaff;
  }

  // Calculate result using Math.ceil if distinctClubCount is not zero
  return isAverage ? Math.ceil(subFilteredStaff / distinctClubCount) : subFilteredStaff;
}

export const calculateSelectedFacilityFilterDataByYear = (
  facilityList: any[],
  year: any,
  firstFilter: string,
  subFilter: string,
  isAverage: boolean,
  filteredClubIds: [],
): number => {
  facilityList = getClubsByYearTemp(year, facilityList, filteredClubIds);

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(facilityList.map(item => item.club?.id));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size > 0 && isAverage ? uniqueClubIds.size : 1;
  if (firstFilter == OPTIONSENUM.TOTALFACILITIES) {
    if (subFilter == OPTIONSENUM.PERCENTAGEOWNED) {
      if (facilityList.length == 0) {
        return 0;
      }
      let ownedFacilities = facilityList.filter(facility => {
        return facility.facilityStatus == 'FS_OWNED';
      });
      let percentageOwned = (ownedFacilities.length / facilityList.length) * 100;
      return percentageOwned;
    } else if (subFilter == OPTIONSENUM.PERCENTAGEPRIORITY) {
      if (facilityList.length == 0) {
        return 0;
      }
      let priorityFacilities = facilityList.filter(facility => {
        return facility.facilityStatus == 'FS_PRIORITY';
      });
      let percentagePriority = (priorityFacilities.length / facilityList.length) * 100;
      return percentagePriority;
    } else if (subFilter == OPTIONSENUM.PERCENTAGEPUBLIC) {
      if (facilityList.length == 0) {
        return 0;
      }
      let publicFacilities = facilityList.filter(facility => {
        return facility.facilityStatus == 'FS_PUBLIC';
      });
      let percentagePublic = (publicFacilities.length / facilityList.length) * 100;
      return percentagePublic;
    } else if (subFilter == OPTIONSENUM.WITHMEDICALROOMS) {
      let medicalRoomFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_MEDICAL_ROOM');
      });
      return medicalRoomFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHDORMITORIES) {
      let dormitoryFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_DORMITORY');
      });
      return dormitoryFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHCHANGINGROOMS) {
      let changingRoomFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_CHANGING_ROOMS');
      });
      return changingRoomFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHCLUBHOUSE) {
      let clubHouseFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_CLUB_HOUSE');
      });
      return clubHouseFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHHOTSHOWERS) {
      let hotShowerFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_HOT_SHOWERS');
      });
      return hotShowerFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHCOLDSHOWERS) {
      let coldShowerFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_COLD_SHOWERS');
      });
      return coldShowerFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHADULTGOALS) {
      let adultGoalsFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_SOCCER_GOAL_POSSESSION_ADULT');
      });
      return adultGoalsFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHJUNIORGOALS) {
      let juniorGoalsFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_SOCCER_GOAL_POSSESSION_JUNIOR');
      });
      return juniorGoalsFacilities.length;
    } else if (subFilter == OPTIONSENUM.WITHKIDSGOALS) {
      let kidsGoalsFacilities = facilityList.filter(facility => {
        return facility.capabilities?.includes('CAP_SOCCER_GOAL_POSSESSION_KIDS');
      });
      return kidsGoalsFacilities.length;
    }
    return facilityList.length / distinctClubCount;
  } else if (firstFilter == OPTIONSENUM.CHANGINGROOMS) {
    let total = 0;
    for (const facility of facilityList) {
      if (facility.changingRooms) {
        total += facility.changingRooms;
      }
    }
    return total / distinctClubCount;
  } else if (firstFilter == OPTIONSENUM.DORMITORYCAPACITY) {
    let total = 0;
    for (const facility of facilityList) {
      if (facility.dormitoryCapacity) {
        total += facility.dormitoryCapacity;
      }
    }
    return total / distinctClubCount;
  } else if (firstFilter == OPTIONSENUM.TOTALPITCHES) {
    if (subFilter == OPTIONSENUM.NATURALPITCHES) {
      let count = 0;
      for (const facility of facilityList) {
        if (facility.facilityGrounds)
          for (const facilityGround of facility.facilityGrounds) {
            if (facilityGround.turf?.includes('TURF_3') || facilityGround.turf?.includes('TURF_8')) {
              count++;
            }
          }
      }
      return count / distinctClubCount;
    } else if (subFilter == OPTIONSENUM.ARTIFICIALPITCHES) {
      let count = 0;
      for (const facility of facilityList) {
        if (facility.facilityGrounds)
          for (const facilityGround of facility.facilityGrounds) {
            if (
              facilityGround.turf?.includes('TURF_2') ||
              facilityGround.turf?.includes('TURF_4') ||
              facilityGround.turf?.includes('TURF_5') ||
              facilityGround.turf?.includes('TURF_6')
            ) {
              count++;
            }
          }
      }
      return count / distinctClubCount;
    } else if (subFilter == OPTIONSENUM.ILLUMINATEDPITCHES) {
      let count = 0;
      for (const facility of facilityList) {
        if (facility.facilityGrounds)
          for (const facilityGround of facility.facilityGrounds) {
            if (facilityGround.illumination?.trim?.().length != 0) {
              count++;
            }
          }
      }
      return count / distinctClubCount;
    } else if (subFilter == OPTIONSENUM.WITHSPECTATORS) {
      let count = 0;
      for (const facility of facilityList) {
        if (facility.facilityGrounds)
          for (const facilityGround of facility.facilityGrounds) {
            if (facilityGround.spectators > 0) {
              count++;
            }
          }
      }
      return count / distinctClubCount;
    } else {
      let total = 0;
      for (const facility of facilityList) {
        if (facility.facilityGrounds) {
          total += facility.facilityGrounds?.length;
        }
      }
      return total / distinctClubCount;
    }
  } else if (firstFilter == OPTIONSENUM.TOTALSPECTATORS) {
    let count = 0;
    for (const facility of facilityList) {
      if (facility.facilityGrounds)
        for (const facilityGround of facility.facilityGrounds) {
          count += facilityGround.spectators;
        }
    }
    return count / distinctClubCount;
  } else if (firstFilter == OPTIONSENUM.TOTALPITCHSQUAREX) {
    let area = 0;
    for (const facility of facilityList) {
      if (facility.facilityGrounds)
        for (const facilityGround of facility.facilityGrounds) {
          area += facilityGround.width * facilityGround.depth;
        }
    }
    return area / distinctClubCount;
  }
  return 0;
};

export const calculateSelectedTeamsFilterDataByYear = (
  teamsList: any[],
  year: any,
  firstFilter: string,
  subFilter: string,
  isAverage: boolean,
  filteredClubIds:any,
): number => {
  // add year in response
  teamsList = getClubsByYearTemp(year, teamsList, filteredClubIds);

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(teamsList.map(item => item.club?.id));

  if (teamsList.length == 0) {
    return 0;
  }

  const getFilteredTeams = (age:any) => {
    let ageGroup = 'AG_U9';

    if (OPTIONSENUM.U9 == age) {
      ageGroup = 'AG_U9';
    } else if (OPTIONSENUM.U10 == age) {
      ageGroup = 'AG_U10';
    } else if (OPTIONSENUM.U11 == age) {
      ageGroup = 'AG_U11';
    } else if (OPTIONSENUM.U12 == age) {
      ageGroup = 'AG_U12';
    } else if (OPTIONSENUM.U13 == age) {
      ageGroup = 'AG_U13';
    } else if (OPTIONSENUM.U14 == age) {
      ageGroup = 'AG_U14';
    } else if (OPTIONSENUM.U15 == age) {
      ageGroup = 'AG_U15';
    } else if (OPTIONSENUM.U16 == age) {
      ageGroup = 'AG_U16';
    } else if (OPTIONSENUM.U17 == age) {
      ageGroup = 'AG_U17';
    } else if (OPTIONSENUM.U18 == age) {
      ageGroup = 'AG_U18';
    }

    const filteredTeamList: any[] = [];
    for (const team of teamsList) {
      if (team?.teamDetails?.ageGroup == ageGroup) {
        filteredTeamList.push(team);
      }
    }

    return filteredTeamList;
  };

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size > 0 && isAverage ? uniqueClubIds.size : 1;
  if (firstFilter == OPTIONSENUM.TOTALTEAMS) {
    if (subFilter && subFilter !== OPTIONSENUM.TOTALTEAMS) {
      const filteredList = getFilteredTeams(subFilter);
      return filteredList?.length;
    } else {
      return isAverage ? Math.ceil(teamsList.length / distinctClubCount) : teamsList.length;
    }
  } else if (firstFilter == OPTIONSENUM.TOTALEXPENSES) {
    let totalPlayersExpense = 0;
    let totalTeamsExpense = 0;
    for (const team of teamsList) {
      totalPlayersExpense += team.teamFees?.playerMonthlyExpense;
      totalTeamsExpense += team.teamFees?.travelMonthlyExpense;
    }
    if (subFilter == OPTIONSENUM.PLAYEREXPENSES) {
      return totalPlayersExpense;
    } else if (subFilter == OPTIONSENUM.TRAVELEXPENSES) {
      return totalTeamsExpense;
    }
    return isAverage
      ? Math.ceil(totalPlayersExpense + totalTeamsExpense / distinctClubCount)
      : totalPlayersExpense + totalTeamsExpense;
  } else if (firstFilter == OPTIONSENUM.PLAYEREXPENSES) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let totalPlayersExpense = 0;
    for (const team of teamsList) {
      totalPlayersExpense += team.teamFees?.playerMonthlyExpense;
    }
    return totalPlayersExpense;
  } else if (firstFilter == OPTIONSENUM.TRAVELEXPENSES) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let totalTeamsExpense = 0;
    for (const team of teamsList) {
      totalTeamsExpense += team.teamFees?.travelMonthlyExpense;
    }
    return totalTeamsExpense;
  } else if (firstFilter == OPTIONSENUM.ADMISSIONFEES) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let totalTeamsFees = 0;
    for (const team of teamsList) {
      totalTeamsFees += team.teamFees?.admissionFees;
    }
    return isAverage ? Math.ceil(totalTeamsFees / distinctClubCount) : totalTeamsFees;
  } else if (firstFilter == OPTIONSENUM.TOTALPLAYERS) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team?.playerAges) {
        for (const key in team?.playerAges) {
          if (key != 'id') {
            total += team?.playerAges[key];
          }
        }
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.COACHESPERTEAM) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.teamDetails) {
        const tempCoaches = team.teamDetails?.staffCoaches.filter((x: any) => x != -1);
        total += tempCoaches.length;
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.GKCOACHESPERTEAM) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.teamDetails) {
        const tempGkCoaches = team.teamDetails?.staffGkCoaches.filter((x: any) => x != -1);
        total += tempGkCoaches.length;
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.MEDICALSTAFFPERTEAM) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.teamDetails) {
        const tempMedicalStaff = team.teamDetails?.medicalStaff.filter((x: any) => x != -1);
        total += tempMedicalStaff.length;
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.SAQUALIFIEDPERTEAM) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.teamDetails) {
        const tempCoaches = team.teamDetails?.staffCoaches.filter((x: any) => x != -1);
        total += tempCoaches.length;
        const tempGkCoaches = team.teamDetails?.staffGkCoaches.filter((x: any) => x != -1);
        total += tempGkCoaches.length;
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.TRAININGHOURS) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let finalHours = 0;
    for (const singleTeam of teamsList) {
      if (singleTeam.trainingSchedule) {
        for (const day in singleTeam.trainingSchedule) {
          if (
            day != 'id' &&
            day != 'year' &&
            singleTeam.trainingSchedule[day].start != undefined &&
            singleTeam.trainingSchedule[day].end != undefined
          ) {
            const start = new Date(singleTeam.trainingSchedule[day].start);
            const end = new Date(singleTeam.trainingSchedule[day].end);
            const startTime = start.getHours() * 3600 + start.getMinutes() * 60 + start.getSeconds();
            const endTime = end.getHours() * 3600 + end.getMinutes() * 60 + end.getSeconds();
            const trainingSeconds = endTime - startTime;
            const trainingHours = trainingSeconds / 3600;
            const positiveValue = trainingHours * (trainingHours < 0 ? -1 : 1);
            finalHours = finalHours + positiveValue;
          }
        }
      }
    }
    return finalHours;
  } else if (firstFilter == OPTIONSENUM.TOTALCOMPETITIONS) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.competitions) {
        total += team.competitions.length;
      }
    }
    return total;
  } else if (firstFilter == OPTIONSENUM.TOURNAMENTWINS) {
    if (subFilter) {
      teamsList = getFilteredTeams(subFilter);
    }
    let total = 0;
    for (const team of teamsList) {
      if (team.competitions) {
        for (const comp of team.competitions) {
          if (comp.position == 'PA_WINNER') {
            total++;
          }
        }
      }
    }
    return total;
  }
  return 0;
};

export const calculateSelectedPlayersFilterDataByYear = (
  playersList: any[],
  year: any,
  firstFilter: string,
  subFilter: string,
  isAverage: boolean,
  filteredClubIds: [],
): number => {
  // add year in response
  playersList = getClubsByYearTemp(year, playersList, filteredClubIds);

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(playersList.map(item => item.club?.id));
  const distinctClubCount = uniqueClubIds.size > 0 ? uniqueClubIds.size : 1;

  if (playersList.length == 0) {
    return 0;
  }
  if (firstFilter == OPTIONSENUM.TOTALPLAYERS) {
    let list = playersList;
    if (subFilter == OPTIONSENUM.INLAST2YEARS) {
      list = playersList?.filter(player => {
        if (player.clubCareers) {
          for (const clubCareer of player.clubCareers) {
            const date = new Date(clubCareer.dateFrom);
            const current = new Date();
            if (date >= new Date(current.setFullYear(new Date().getFullYear() - 2))) {
              return true;
            }
          }
        }
        return false;
      });
    } else if (subFilter == OPTIONSENUM.INLAST5YEARS) {
      list = playersList?.filter(player => {
        if (player.clubCareers) {
          for (const clubCareer of player.clubCareers) {
            const date = new Date(clubCareer.dateFrom);
            const current = new Date();
            if (date >= new Date(current.setFullYear(new Date().getFullYear() - 5))) {
              return true;
            }
          }
        }
        return false;
      });
    }
    return calculateResult(list.length, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.AVERAGETIMEATACADEMY) {
    let totalYears = 0;
    const numberOfTeams = playersList.length ?? 1;
    for (const player of playersList) {
      if (player.academyPeriods) {
        for (const academyPeriods of player.academyPeriods) {
          const dateFrom = academyPeriods.dateFrom != null ? new Date(academyPeriods.dateFrom) : new Date();
          const dateTo = academyPeriods.dateTo != null ? new Date(academyPeriods.dateTo) : new Date();
          totalYears = totalYears + dateTo.getFullYear() - dateFrom.getFullYear();
        }
      }
    }
    return totalYears / numberOfTeams;
  } else if (firstFilter == OPTIONSENUM.PLAYERSPROMOTEDTO1STLIST) {
    let list: any = [];
    playersList.forEach(player => {
      let tempEarlyCareer: any = undefined;
      let thisClub = clubsData.find((cd: any) => cd.id == player.club.id);
      if (player?.clubCareers != undefined && player.clubCareers != null && player.clubCareers) {
        player.clubCareers.forEach((cc: any) => {
          if (
            (tempEarlyCareer == undefined && (cc?.dateFrom != null || cc?.dateFrom != undefined)) ||
            new Date(cc?.dateFrom) < new Date(tempEarlyCareer?.dateFrom)
          ) {
            tempEarlyCareer = cc;
          }
        });
        if (
          (tempEarlyCareer?.club != undefined || tempEarlyCareer?.club != null) &&
          (thisClub?.jname.toLowerCase() == tempEarlyCareer?.club.toLowerCase() ||
            thisClub?.ename.toLowerCase() == tempEarlyCareer?.club.toLowerCase())
        ) {
          list.push(tempEarlyCareer);
        }
        tempEarlyCareer = undefined;
      }
    });
    if (subFilter == OPTIONSENUM.INLAST2YEARS) {
      var tempList = list.filter((career: any) => {
        if (career) {
          const date = new Date(career.dateFrom);
          const current = new Date();
          if (date >= new Date(current.setFullYear(new Date().getFullYear() - 2))) {
            return true;
          }
        }
        return false;
      });
      return calculateResult(tempList.length, distinctClubCount, isAverage);
    } else if (subFilter == OPTIONSENUM.INLAST5YEARS) {
      var tempList = list.filter((career: any) => {
        if (career) {
          const date = new Date(career.dateFrom);
          const current = new Date();
          if (date >= new Date(current.setFullYear(new Date().getFullYear() - 5))) {
            return true;
          }
        }
        return false;
      });
      return calculateResult(tempList.length, distinctClubCount, isAverage);
    }
    return calculateResult(list.length, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.PLAYERSAPEARINGFOROTHERCLUBS) {
    let list = playersList.filter(player => {
      let thisClub = clubsData.find((cd: any) => cd.id == player.club.id);
      if (
        player.clubCareers?.length != undefined &&
        player.clubCareers?.length != null &&
        player.clubCareers?.length > 0
      ) {
        const otherCareer = player.clubCareers.some(
          (c:any) =>
            c?.club != undefined &&
            c?.club != null &&
            thisClub?.jname.toLowerCase() != c?.club.toLowerCase() &&
            thisClub?.ename.toLowerCase() != c?.club.toLowerCase(),
        );
        if (otherCareer) {
          return true;
        }
      }
      return false;
    });
    const currentDate = new Date();
    if (subFilter == OPTIONSENUM.INLAST2YEARS) {
      list = list?.filter(player => {
        let thisClub = clubsData.find((cd: any) => cd.id == player.club.id);
        if (
          player.clubCareers?.length != undefined &&
          player.clubCareers?.length != null &&
          player.clubCareers?.length > 0
        ) {
          const otherCareer = player.clubCareers.some(
            (c:any) =>
              c?.club != undefined &&
              c?.club != null &&
              thisClub?.jname.toLowerCase() != c?.club.toLowerCase() &&
              thisClub?.ename.toLowerCase() != c?.club.toLowerCase() &&
              new Date(c.dateFrom && c.dateFrom) >= new Date(currentDate.setFullYear(new Date().getFullYear() - 2)),
          );
          if (otherCareer) {
            return true;
          }
        }
        return false;
      });
    } else if (subFilter == OPTIONSENUM.INLAST5YEARS) {
      list = list?.filter(player => {
        let thisClub = clubsData.find((cd: any) => cd.id == player.club.id);
        if (
          player.clubCareers?.length != undefined &&
          player.clubCareers?.length != null &&
          player.clubCareers?.length > 0
        ) {
          const otherCareer = player.clubCareers.some(
            (c:any) =>
              c?.club != undefined &&
              c?.club != null &&
              thisClub?.jname.toLowerCase() != c?.club.toLowerCase() &&
              thisClub?.ename.toLowerCase() != c?.club.toLowerCase() &&
              new Date(c.dateFrom && c.dateFrom) >= new Date(currentDate.setFullYear(new Date().getFullYear() - 5)),
          );
          if (otherCareer) {
            return true;
          }
        }
        return false;
      });
    }
    return calculateResult(list.length, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.INTERNATIONALPLAYERS) {
    let list = playersList.filter(player => {
      return player.internationalCareers?.length > 0;
    });
    if (subFilter == OPTIONSENUM.INLAST2YEARS) {
      list = list.filter(team => {
        for (const internationalCareer of team.internationalCareers) {
          const date = new Date(internationalCareer.dateFrom);
          const current = new Date();
          if (date >= new Date(current.setFullYear(new Date().getFullYear() - 2))) {
            return true;
          }
        }

        return false;
      });
    } else if (subFilter == OPTIONSENUM.INLAST5YEARS) {
      list = list.filter(player => {
        for (const internationalCareer of player.internationalCareers) {
          const date = new Date(internationalCareer.dateFrom);
          const current = new Date();
          if (date >= new Date(current.setFullYear(new Date().getFullYear() - 5))) {
            return true;
          }
        }
        return false;
      });
    }
    return calculateResult(list.length, distinctClubCount, isAverage);
  } else if (firstFilter == OPTIONSENUM.YOUTHINTERNATIONALLAST5YEARS) {
    let ageGroup:any;

    if (subFilter) {
      if (OPTIONSENUM.U9 == subFilter) {
        ageGroup = AgeGroup.AG_U9;
      } else if (OPTIONSENUM.U10 == subFilter) {
        ageGroup = AgeGroup.AG_U10;
      } else if (OPTIONSENUM.U11 == subFilter) {
        ageGroup = AgeGroup.AG_U11;
      } else if (OPTIONSENUM.U12 == subFilter) {
        ageGroup = AgeGroup.AG_U12;
      } else if (OPTIONSENUM.U13 == subFilter) {
        ageGroup = AgeGroup.AG_U13;
      } else if (OPTIONSENUM.U14 == subFilter) {
        ageGroup = AgeGroup.AG_U14;
      } else if (OPTIONSENUM.U15 == subFilter) {
        ageGroup = AgeGroup.AG_U15;
      } else if (OPTIONSENUM.U16 == subFilter) {
        ageGroup = AgeGroup.AG_U16;
      } else if (OPTIONSENUM.U17 == subFilter) {
        ageGroup = AgeGroup.AG_U17;
      } else if (OPTIONSENUM.U18 == subFilter) {
        ageGroup = AgeGroup.AG_U18;
      }
    }
    let count = 0;
    const currentDate = new Date(); // Get the current date
    playersList.forEach(player => {
      // Check if player has clubCareers array and it's not empty
      if (player.internationalCareers?.length > 0) {
        // Iterate over each club career entry
        player.internationalCareers.forEach((item:any) => {
          // Check if dateFrom is not null or undefined
          if (ageGroup == undefined || (ageGroup && item.ageGroup == ageGroup)) {
            const dateFrom = new Date(item.dateFrom);
            // Check if dateFrom is within the past 5 years
            if (dateFrom.getFullYear() >= currentDate.getFullYear() - 5) {
              count++;
              // Exit loop once a relevant dateFrom is found
              return;
            }
          }
        });
      }
    });
    return count;
  }

  return 0;
};

const calculateByTab = (
  tab: string,
  clubOrLeagueList: any,
  year: any,
  firstFilter: string,
  subFilter: string,
  actualOrBudget: ACTUALBUDGET,
  isAverage: boolean,
  filteredClubIds: [] | any,
) => {
  if (tab == PAGE.FINANCE) {
    return calculateSelectedFilterDataByYear(
      clubOrLeagueList,
      year,
      firstFilter,
      subFilter,
      actualOrBudget,
      isAverage,
      filteredClubIds,
    );
  } else if (tab == PAGE.STAFF) {
    return calculateSelectedStaffFilterDataByYear(
      clubOrLeagueList,
      year,
      firstFilter,
      subFilter,
      isAverage,
      filteredClubIds,
    );
  } else if (tab == PAGE.FACILITIES) {
    return calculateSelectedFacilityFilterDataByYear(
      clubOrLeagueList,
      year,
      firstFilter,
      subFilter,
      isAverage,
      filteredClubIds,
    );
  } else if (tab == PAGE.TEAMS) {
    return calculateSelectedTeamsFilterDataByYear(
      clubOrLeagueList,
      year,
      firstFilter,
      subFilter,
      isAverage,
      filteredClubIds,
    );
  } else if (tab == PAGE.PLAYERS) {
    return calculateSelectedPlayersFilterDataByYear(
      clubOrLeagueList,
      year,
      firstFilter,
      subFilter,
      isAverage,
      filteredClubIds,
    );
  }
  return 0;
};

export const calculateTimeLineDataByYears = (
  tab: PAGE,
  yearValue: SingleOption | undefined,
  leagues: SingleOption[],
  club: ClubOption | undefined,
  ratings: SingleOption[],
  firstFilter: string,
  subFilter: string,
  financeData: [],
  actualOrBudget: ACTUALBUDGET,
  colorsList: Record<string, string>,
  t:any,
  filteredClubIds: [],
  state:any,
):
  | {
      years: string[];
      series: {
        name: string;
        data: number[];
      }[];
      colors: string[];
    }
  | undefined => {
  const series: { name: string; data: number[] }[] = [];
  const clubValues: number[] = [];
  const colors: string[] = [];
  const clubsList = club?.id ? getClubsById(tab, club?.id ?? '', financeData) : financeData;
  if (!yearValue) {
    return;
  }
  const leagueDataHolder: Record<string, { value: number[]; leagueList: ClubData[] }> = {};
  let getLeagueId: (data:any) => {} | undefined;
  if (tab == PAGE.STAFF || tab == PAGE.FACILITIES || tab == PAGE.TEAMS || tab == PAGE.PLAYERS) {
    getLeagueId = data => data?.club?.league?.id;
  }
  leagues?.forEach(league => {
    leagueDataHolder[league.name] = { value: [], leagueList: getLeagueById(league.id, financeData, getLeagueId) };
  });
  const ratingDataHolder: Record<string, { value: number[]; ratingList: ClubData[] }> = {};
  ratings?.forEach(rating => {
    ratingDataHolder[rating.name] = { value: [], ratingList: getClubsByRating(rating.id, financeData) };
  });
  let currentYear = yearValue.id;
  let years = getLastFiveYear(currentYear);
  const allClubIds: any = getClubOptions(state).map((item:any) => item?.id);
  years?.forEach(year => {
    const clubIds = fetchClubIdsByStaticData(state, { id: year });
    clubValues.push(calculateByTab(tab, clubsList, year, firstFilter, subFilter, actualOrBudget, true, clubIds));
    leagues?.forEach(league => {
      const value = calculateByTab(
        tab,
        leagueDataHolder[league.name].leagueList,
        year,
        firstFilter,
        subFilter,
        actualOrBudget,
        true,
        allClubIds,
      );
      leagueDataHolder[league.name].value.push(value);
    });
    ratings?.forEach(rating => {
      const value = calculateByTab(
        tab,
        ratingDataHolder[rating.name].ratingList,
        year,
        firstFilter,
        subFilter,
        actualOrBudget,
        true,
        allClubIds,
      );
      ratingDataHolder[rating.name].value.push(value);
    });
  });
  if (club) {
    series.push({ name: club.name, data: clubValues });
    colors.push(colorsList['club']);
  }
  Object.keys(leagueDataHolder)?.forEach(key => {
    series.push({ name: key + ' ' + t('AVERAGE'), data: leagueDataHolder[key].value });
    colors.push(colorsList[key]);
  });
  Object.keys(ratingDataHolder)?.forEach(key => {
    colors.push(colorsList[key]);
    series.push({ name: key + ' ' + t('AVERAGE'), data: ratingDataHolder[key].value });
  });
  if (!club && Object.keys(leagueDataHolder)?.length === 0 && Object.keys(ratingDataHolder)?.length === 0) {
    series.push({ name: t('Clubs') + ' ' + t('AVERAGE'), data: clubValues });
    colors.push(colorsList['avg']);
  }

  if (tab == PAGE.FINANCE && actualOrBudget == ACTUALBUDGET.ACTUAL) {
    currentYear = currentYear - 1;
  }
  years = getLastFiveYear(currentYear);
  return { years: years, series: series, colors: colors };
};

export const calculateSingleRankingData = (
  tab: string,
  dataList: ClubData[],
  year: string,
  actualOrBudget: ACTUALBUDGET,
  filteredClubIds: [],
): number[] => {
  const value: number[] = [];
  value.push(calculateByTab(tab, dataList, year, OPTIONSENUM.CLUBSPEND, '', actualOrBudget, true, filteredClubIds));
  value.push(calculateByTab(tab, dataList, year, OPTIONSENUM.ACADEMYSPEND, '', actualOrBudget, true, filteredClubIds));
  value.push(
    calculateByTab(tab, dataList, year, OPTIONSENUM.CLUBSPENDONACADEMY, '', actualOrBudget, true, filteredClubIds),
  );
  value.push(calculateByTab(tab, dataList, year, OPTIONSENUM.LABOURCOST, '', actualOrBudget, true, filteredClubIds));
  value.push(calculateByTab(tab, dataList, year, OPTIONSENUM.ACADEMYINCOME, '', actualOrBudget, true, filteredClubIds));
  return value;
};

const sortRanking = (a:any, b:any) => {
  if (a.value < b.value) {
    return -1;
  } else if (a.value > b.value) {
    return 1;
  }
  return 0;
};

/**
 * Calculate all selected club and league and rating values for ranking graph
 * @param yearValue
 * @param leagues
 * @param club
 * @param ratings
 * @returns
 */
export const sortAllClubs = (
  tab: string,
  yearValue: SingleOption | undefined,
  financeData: any[],
  actualOrBudget: ACTUALBUDGET,
  allClubs: ClubOption[],
  filteredClubIds: [],
):
  | {
      [OPTIONSENUM.CLUBSPEND]: any[];
      [OPTIONSENUM.ACADEMYSPEND]: any[];
      [OPTIONSENUM.CLUBSPENDONACADEMY]: any[];
      [OPTIONSENUM.LABOURCOST]: any[];
      [OPTIONSENUM.ACADEMYINCOME]: any[];
    }
  | undefined => {
  if (!(allClubs && allClubs.length > 0 && financeData && financeData.length > 0)) {
    return;
  }
  const clubSpendRanking: any[] = [];
  const academySpendRanking: any[] = [];
  const clubSpendOnAcademyRanking: any[] = [];
  const labourcostRanking: any[] = [];
  const academyIncomeRanking: any[] = [];
  const year = yearValue ? yearValue.id : '2023';

  allClubs.forEach(club => {
    clubSpendRanking.push({
      key: club,
      value: calculateByTab(tab, financeData, year, OPTIONSENUM.CLUBSPEND, '', actualOrBudget, true, filteredClubIds),
    });
    academySpendRanking.push({
      key: club,
      value: calculateByTab(
        tab,
        financeData,
        year,
        OPTIONSENUM.ACADEMYSPEND,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      ),
    });
    clubSpendOnAcademyRanking.push({
      key: club,
      value: calculateByTab(
        tab,
        financeData,
        year,
        OPTIONSENUM.CLUBSPENDONACADEMY,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      ),
    });
    labourcostRanking.push({
      key: club,
      value: calculateByTab(tab, financeData, year, OPTIONSENUM.LABOURCOST, '', actualOrBudget, true, filteredClubIds),
    });
    academyIncomeRanking.push({
      key: club,
      value: calculateByTab(
        tab,
        financeData,
        year,
        OPTIONSENUM.ACADEMYINCOME,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      ),
    });
  });

  clubSpendRanking.sort(sortRanking);
  academySpendRanking.sort(sortRanking);
  clubSpendOnAcademyRanking.sort(sortRanking);
  labourcostRanking.sort(sortRanking);
  academyIncomeRanking.sort(sortRanking);
  return {
    [OPTIONSENUM.CLUBSPEND]: clubSpendRanking,
    [OPTIONSENUM.ACADEMYSPEND]: academySpendRanking,
    [OPTIONSENUM.CLUBSPENDONACADEMY]: clubSpendOnAcademyRanking,
    [OPTIONSENUM.LABOURCOST]: labourcostRanking,
    [OPTIONSENUM.ACADEMYINCOME]: academyIncomeRanking,
  };
};

export const totalAcademySpend = (
  tab: PAGE,
  club: ClubOption | undefined,
  leagueValue: SingleOption[],
  starValue: SingleValueOption[],
  yearValue: SingleOption | undefined,
  financeData: [],
  filteredClubIds: [],
  actualOrBudget: ACTUALBUDGET,
): {} => {
  const filteredData = getClubsByYearTemp(yearValue?.name ?? '', financeData, filteredClubIds);

  if (club) {
    const clubData = getClubsById(tab, club.id, filteredData);
    const value = shortenNumber(getAcademySpendTotal(clubData, actualOrBudget));
    return { value: value };
  } else if (leagueValue?.length > 0) {
    const leagueData = getClubsByLeagues(leagueValue, filteredData);
    const value = shortenNumber(getAcademySpendTotal(leagueData, actualOrBudget));
    return { value: value };
  } else if (starValue?.length > 0) {
    const starData = getClubsByStars(starValue, filteredData);
    const value = shortenNumber(getAcademySpendTotal(starData, actualOrBudget));
    return { value: value };
  } else {
    const value = shortenNumber(getAcademySpendTotal(filteredData, actualOrBudget));
    return { value: value };
  }
};

// Filter and calculate club spend on academy %
export const clubSpendOnAcademy = (
  tab: PAGE,
  clubValue: ClubOption | undefined,
  leagueValue: SingleOption[] | any,
  starValue: SingleValueOption[],
  yearValue: SingleOption | undefined,
  financeData: [],
  filteredClubIds: [],
  actualOrBudget: ACTUALBUDGET,
): { value: string; yoy: number } => {
  const filteredData = getClubsByYearTemp(yearValue?.name ?? '', financeData, filteredClubIds);
  if (clubValue) {
    const clubData = getClubsById(tab, clubValue.id, filteredData);
    const academySpendTotal = getAcademySpendTotal(clubData, actualOrBudget);
    const clubSpendTotal = getSpendTotal(clubData, actualOrBudget);
    const percentageSpent = clubSpendTotal > 0 ? (academySpendTotal / clubSpendTotal) * 100 : 0;
    const value = percentageSpent ? percentageSpent.toFixed(1) : '0';
    const yoy = getYOYClubSpend(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else if (leagueValue?.length > 0) {
    const filteredLeagues = getClubsByLeagues(leagueValue, filteredData);
    const academySpendTotal = getAcademySpendTotal(filteredLeagues, actualOrBudget);
    const clubSpendTotal = getSpendTotal(filteredLeagues, actualOrBudget);
    const percentageSpent = clubSpendTotal > 0 ? (academySpendTotal / clubSpendTotal) * 100 : 0;
    const value = percentageSpent ? percentageSpent.toFixed(1) : '0';
    const yoy = getYOYClubSpend(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else if (starValue.length > 0) {
    const filteredStars = getClubsByStars(starValue, filteredData);
    const academySpendTotal = getAcademySpendTotal(filteredStars, actualOrBudget);
    const clubSpendTotal = getSpendTotal(filteredStars, actualOrBudget);
    const percentageSpent = clubSpendTotal > 0 ? (academySpendTotal / clubSpendTotal) * 100 : 0;
    const value = percentageSpent ? percentageSpent.toFixed(1) : '0';
    const yoy = getYOYClubSpend(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else {
    const academySpendTotal = getAcademySpendTotal(filteredData, actualOrBudget);
    const clubSpendTotal = getSpendTotal(filteredData, actualOrBudget);
    const percentageSpent = clubSpendTotal > 0 ? (academySpendTotal / clubSpendTotal) * 100 : 0;
    const value = percentageSpent ? percentageSpent.toFixed(1) : '0';
    const yoy = getYOYClubSpend(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  }
};

// Academy profitability average
export const academyProfitability = (
  tab: PAGE,
  clubValue: ClubOption | undefined,
  leagueValue: SingleOption[],
  starValue: SingleValueOption[],
  yearValue: SingleOption | undefined,
  financeData: [],
  filteredClubIds: [],
  actualOrBudget: ACTUALBUDGET,
): {} => {
  const filteredData = getClubsByYearTemp(yearValue?.name ?? '', financeData, filteredClubIds);
  if (clubValue) {
    const clubData = getClubsById(tab, clubValue.id, filteredData);
    const value = shortenNumber(calculateAcademyProfitability(clubData, actualOrBudget));
    const yoy = getYOYAcadProfit(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else if (leagueValue?.length > 0) {
    const leagueData = getClubsByLeagues(leagueValue, filteredData);
    const value = shortenNumber(calculateAcademyProfitability(leagueData, actualOrBudget));
    const yoy = getYOYAcadProfit(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else if (starValue.length > 0) {
    const starData = getClubsByStars(starValue, filteredData);
    const value = shortenNumber(calculateAcademyProfitability(starData, actualOrBudget));
    const yoy = getYOYAcadProfit(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  } else {
    const value = shortenNumber(calculateAcademyProfitability(filteredData, actualOrBudget));
    const yoy = getYOYAcadProfit(financeData, actualOrBudget);
    return { value: value, yoy: yoy };
  }
};

const getMaxMinValue = (
  tab: PAGE,
  clubSelected: ClubOption | undefined,
  clubList: ClubOption[],
  year: string,
  firstFilter: any | undefined,
  subFilter: any | undefined,
  actualOrBudget: ACTUALBUDGET,
  financeData: ClubData[],
  filteredClubIds: [],
): { max: number; min: number; clubVal: number; avgClubVal: number } => {
  let max = 0;
  let min = 0;
  let clubVal = 0;
  let avgClubVal = calculateByTab(
    tab,
    financeData,
    year,
    firstFilter?.id ?? '',
    subFilter?.id ?? '',
    actualOrBudget,
    true,
    filteredClubIds,
  );
  if (tab == PAGE.STAFF || tab == PAGE.TEAMS || tab == PAGE.PLAYERS) {
    const uniqueClubIds = new Set(financeData.map(item => item.club?.id));
    const distinctClubCount = uniqueClubIds.size > 0 ? uniqueClubIds.size : 1;
    avgClubVal = avgClubVal / distinctClubCount;
  }
  clubList.forEach((club: ClubOption) => {
    const financeDataByClub: ClubData[] = getClubsById(tab, club.id, financeData);
    const value: number = calculateByTab(
      tab,
      financeDataByClub,
      year,
      firstFilter?.id ?? 0,
      subFilter?.id ?? '',
      actualOrBudget,
      true,
      filteredClubIds,
    );
    if (max < value) {
      max = value;
    }
    if (min > value) {
      min = value;
    }
    if (club?.id == clubSelected?.id) {
      clubVal = value;
    }
  });
  return { max: max, min: min, clubVal: clubVal, avgClubVal: avgClubVal };
};
const claculateBenchmarkDataEntry = (
  tab: PAGE,
  yearValue: SingleOption | undefined,
  leagues: SingleOption[],
  club: ClubOption | undefined,
  ratings: SingleOption[],
  firstFilter: SingleOption | undefined,
  subFilter: any,
  financeData: ClubData[],
  actualOrBudget: ACTUALBUDGET,
  clubList: ClubOption[],
  filteredClubIds: [],
): { x: string; y: number[]; z: number } | undefined => {
  const maxMinVal = getMaxMinValue(
    tab,
    club,
    clubList,
    yearValue?.name ?? '',
    firstFilter,
    subFilter,
    actualOrBudget,
    financeData,
    filteredClubIds,
  );

  const x = subFilter ? (subFilter.name ? subFilter.name : subFilter?.label) : firstFilter?.name;
  if (!x) {
    return;
  }
  const val25 = (maxMinVal.max - maxMinVal.min) * 0.25 + maxMinVal.min;
  const val75 = (maxMinVal.max - maxMinVal.min) * 0.75 + maxMinVal.min;
  return {
    x: x,
    y: [maxMinVal.min, val25, maxMinVal.avgClubVal, val75, maxMinVal.max],
    z: maxMinVal.clubVal,
  };
};
export const claculateBenchmarkData = (
  tab: PAGE,
  yearValue: SingleOption | undefined,
  leagues: SingleOption[],
  club: ClubOption | undefined,
  ratings: SingleOption[],
  firstFilter: SingleOption | undefined,
  subFilters: SingleOption[] | undefined,
  financeData: ClubData[],
  actualOrBudget: ACTUALBUDGET,
  clubList: ClubOption[],
  filteredClubIds: [],
): [{ x: string; y: number[] }[], { x: string; y: number }[]] => {
  const seriesData: { x: string; y: number[]; z: number }[] = [];
  const clubData: { x: string; y: number }[] = [];
  if (leagues?.length > 0 || ratings.length > 0) {
    clubList = leagues?.length > 0 ? getClubsByFilter(leagues, clubList) : getClubsByFilter(ratings, clubList);
    financeData = leagues?.length > 0 ? getClubsByLeagues(leagues, financeData) : getClubsByStars(ratings, financeData);
  }
  if (subFilters && subFilters.length > 0) {
    subFilters?.forEach(subFilter => {
      const entry = claculateBenchmarkDataEntry(
        tab,
        yearValue,
        leagues,
        club,
        ratings,
        firstFilter,
        subFilter,
        financeData,
        actualOrBudget,
        clubList,
        filteredClubIds,
      );
      if (entry) {
        seriesData.push({ x: entry.x, y: entry.y, z: entry.z });
        if (entry.z != undefined) {
          clubData.push({ x: entry.x, y: entry.z });
        }
      }
    });
  } else {
    const entry = claculateBenchmarkDataEntry(
      tab,
      yearValue,
      leagues,
      club,
      ratings,
      firstFilter,
      undefined,
      financeData,
      actualOrBudget,
      clubList,
      filteredClubIds,
    );
    if (entry) {
      seriesData.push({ x: entry.x, y: entry.y, z: entry.z });
      if (entry.z != undefined) {
        clubData.push({ x: entry.x, y: entry.z });
      }
    }
  }
  return [seriesData, clubData];
};

export const getSelectedClubPlayerCount = (clubId:any, playersList:any, teamsData:any, xaxis:any): number => {
  if (OPTIONSENUM.TOURNAMENTWINS == xaxis.id) {
    let total = 0;
    for (const team of teamsData) {
      if (team.club.id == clubId && team.competitions) {
        for (const comp of team.competitions) {
          if (comp.position == PositionAchieved.PA_WINNER) {
            total++;
          }
        }
      }
    }
    return total;
  }
  const list = playersList?.filter((player:any) => {
    if (player.club.id == clubId && player.clubCareers) {
      for (const clubCareer of player.clubCareers) {
        const date = new Date(clubCareer.dateFrom);
        const current = new Date();
        if (date >= new Date(current.setFullYear(new Date().getFullYear() - 2))) {
          return true;
        }
      }
    }
    return false;
  });
  if (OPTIONSENUM.IPLAYERSPRODUCED == xaxis.id) {
    const ilist = list.filter((team:any) => {
      if (team?.internationalCareers?.length) {
        return true;
      }
      return false;
    });
    return ilist.length;
  }
  return list.length;
};

// ROI logic
export const claculateROIData = (
  tab: PAGE,
  yearValue: SingleOption | undefined,
  leagues: SingleOption[],
  selectedClub: ClubOption | undefined,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  allclubsList: ClubOption[],
  response: ClubData[],
  xaxis: SingleOption | undefined,
  subyaxis: any,
  yaxis: any,
  playersData: any,
  teamsData: any,
  t: (string:any) => string,
  filteredClubIds: {} | any,
): { label: string; data: { x: number; y: number }[] }[] => {
  const series: { label: string; data: { x: number; y: number; club: string }[] }[] = [];

  if (response?.length > 0) {
    if (selectedClub) {
      const currentClub = getClubsById(tab, selectedClub.id, response);
      const investmentData = calculateByTab(
        tab,
        currentClub,
        yearValue?.id,
        yaxis?.id,
        subyaxis?.id,
        actualBudget,
        true,
        filteredClubIds['club'],
      );
      const returnData = getSelectedClubPlayerCount(selectedClub.id, playersData['club'], teamsData['club'], xaxis);
      series.push({ label: selectedClub.name, data: [{ x: returnData, y: investmentData, club: selectedClub.name }] });
    }
    if (leagues) {
      leagues?.map(league => {
        const finalLeagueROI: { x: number; y: number; club: string }[] = [];
        const leaguesData: ClubOption[] = getClubOptionsByLeagueROI(filteredClubIds['leagues'], allclubsList);
        leaguesData.map(item => {
          if (selectedClub?.id == item.id) {
            return;
          }
          const currentClub = getClubsById(tab, item.id, response);
          const investmentData = calculateByTab(
            tab,
            currentClub,
            yearValue?.id,
            yaxis?.id,
            subyaxis?.id,
            actualBudget,
            true,
            filteredClubIds['leagues'],
          );
          const returnData = getSelectedClubPlayerCount(item.id, playersData['leagues'], teamsData['leagues'], xaxis);
          finalLeagueROI.push({ x: returnData, y: investmentData, club: item.name });
        });
        series.push({ label: league.name, data: finalLeagueROI });
      });
    }
    if (ratings) {
      ratings.map(rating => {
        const finalRatingROI: { x: number; y: number; club: string }[] = [];
        const ratingsData = allclubsList.filter(club => club?.targetRating == rating.id.toString());
        ratingsData.map(item => {
          if (selectedClub?.id == item.id) {
            return;
          }
          const currentClub = getClubsById(tab, item.id, response);
          const investmentData = calculateByTab(
            tab,
            currentClub,
            yearValue?.id,
            yaxis?.id,
            subyaxis?.id,
            actualBudget,
            true,
            filteredClubIds['all'],
          );
          const returnData = getSelectedClubPlayerCount(item.id, playersData['all'], teamsData['all'], xaxis);

          finalRatingROI.push({ x: returnData, y: investmentData, club: item.name });
        });
        series.push({ label: rating.name, data: finalRatingROI });
      });
    }
    if (series.length == 0) {
      const allClubROI: { x: number; y: number; club: string }[] = [];
      allclubsList?.map(item => {
        if (item.id == selectedClub?.id) {
          return;
        }
        const currentClub = getClubsById(tab, item.id, response);
        const investmentData = calculateByTab(
          tab,
          currentClub,
          yearValue?.id,
          yaxis?.id,
          subyaxis?.id,
          actualBudget,
          true,
          filteredClubIds['all'],
        );
        const returnData = getSelectedClubPlayerCount(item.id, playersData['all'], teamsData['all'], xaxis);

        allClubROI.push({ x: returnData, y: investmentData, club: item.name });
      });
      series.push({ label: t('ALL_CLUBS'), data: allClubROI });
    }
  }
  return series;
};

// Methods for ranking
export function getAllMetricsValue(
  tab: string,
  club: ClubOption,
  response: any[],
  year: any,
  actualOrBudget: ACTUALBUDGET,
  filters: any,
  filteredClubIds: [],
): any {
  let clubData: any[] = [];
  if (tab == PAGE.STAFF || tab == PAGE.TEAMS || tab == PAGE.PLAYERS) {
    clubData = response?.filter(data => {
      return data.clubId == club.id;
    });
  } else if (tab == PAGE.FACILITIES) {
    clubData = response?.filter(data => {
      return data.club?.id == club.id;
    });
  } else {
    clubData = response?.filter(data => data.clubId == club.id && data.year == year);
  }
  const result:any = {};
  if (clubData) {
    result['clubId'] = club.id;
    result['leagueId'] = club?.leagueId;
    result['currentRating'] = club?.targetRating;
    if (tab == PAGE.STAFF) {
      for (const label of filters) {
        result[label] = calculateByTab(tab, clubData, year, label, '', actualOrBudget, true, filteredClubIds);
      }
    } else if (tab == PAGE.FACILITIES) {
      result[OPTIONSENUM.WITHMEDICALROOMS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALFACILITIES,
        OPTIONSENUM.WITHMEDICALROOMS,
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.CHANGINGROOMS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.CHANGINGROOMS,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.NATURALPITCHES] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALPITCHES,
        OPTIONSENUM.NATURALPITCHES,
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.ARTIFICIALPITCHES] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALPITCHES,
        OPTIONSENUM.ARTIFICIALPITCHES,
        actualOrBudget,
        true,
        filteredClubIds,
      );
    } else if (tab == PAGE.TEAMS) {
      result[OPTIONSENUM.TOTALTEAMS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALTEAMS,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.TOTALEXPENSES] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALEXPENSES,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.ADMISSIONFEES] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.ADMISSIONFEES,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.TOTALPLAYERS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALPLAYERS,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.TRAININGHOURS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TRAININGHOURS,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
    } else if (tab == PAGE.PLAYERS) {
      result[OPTIONSENUM.INLAST2YEARS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALPLAYERS,
        OPTIONSENUM.INLAST2YEARS,
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.INLAST5YEARS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.TOTALPLAYERS,
        OPTIONSENUM.INLAST5YEARS,
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.AVERAGETIMEATACADEMY] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.AVERAGETIMEATACADEMY,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.INTERNATIONALPLAYERS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.INTERNATIONALPLAYERS,
        OPTIONSENUM.INLAST2YEARS,
        actualOrBudget,
        true,
        filteredClubIds,
      );
      result[OPTIONSENUM.YOUTHINTERNATIONALLAST5YEARS] = calculateByTab(
        tab,
        clubData,
        year,
        OPTIONSENUM.YOUTHINTERNATIONALLAST5YEARS,
        '',
        actualOrBudget,
        true,
        filteredClubIds,
      );
    } else {
      const academySpendTotal = getAcademySpendTotal(clubData, actualOrBudget);
      const clubSpendTotal = getSpendTotal(clubData, actualOrBudget);
      const percentageSpent = clubSpendTotal > 0 ? (academySpendTotal / clubSpendTotal) * 100 : 0;
      const value = percentageSpent ? percentageSpent.toFixed(1) : '0';
      result[OPTIONSENUM.CLUBSPEND] = clubSpendTotal;
      result[OPTIONSENUM.ACADEMYSPEND] = academySpendTotal;
      result[OPTIONSENUM.CLUBSPENDONACADEMY] = value;
      result[OPTIONSENUM.LABOURCOST] = getAcademySpendSub(clubData, subType.L, actualOrBudget);
      result[OPTIONSENUM.ACADEMYINCOME] = getAcademyIncomeTotal(clubData, actualOrBudget);
    }
  }
  return result;
}

// Function to calculate metric averages
function calculateMetricAverages(data: any, metricSums: Record<string, number>): Record<string, number> {
  const metricAverages: Record<string, number> = {};

  // Looping through the data and summing up the values
  data.forEach((club:any) => {
    const ranking = club.clubRanking;

    // Add each metric from clubRanking to the corresponding sum in metricSums
    metricSums.clubspend += ranking.clubspend;
    metricSums.academyspend += ranking.academyspend;
    metricSums.clubspendonacademy += ranking.clubspendonacademy;
    metricSums.labourcost += ranking.labourcost;
    metricSums.academyincome += ranking.academyincome;
  });

  // Calculate the averages for each metric
  for (const metric in metricSums) {
    metricAverages[metric] = Number((metricSums[metric] / Object.keys(data)?.length).toFixed());
  }

  return metricAverages;
}

export const calculateRankingForMetrics = (
  tab: PAGE,
  rankingData:any,
  clubValue:any,
  leagues:any,
  ratings:any,
  colorsList:any,
  filters: any[],
  t:any,
  leaguesHistoryClub: any,
):
  | {
      series: {
        name: string;
        data: number[];
      }[];
      colors: string[];
    }
  | undefined => {
  const series: { name: string; data: number[] }[] = [];
  const colors: string[] = [];

  if (clubValue) {
    const clubId = clubValue?.id;
    const selectedClubRanking = rankingData && getClubsById(tab, clubId, rankingData, data => data.clubId);
    const clubRanking = selectedClubRanking.length > 0 && selectedClubRanking[0]?.clubRanking;
    if (clubRanking) {
      const data: number[] = [];
      for (const filter of filters) {
        data.push(clubRanking[filter]);
      }
      series.push({ name: clubValue.name, data: data });
    }
    colors.push(colorsList['club']);
  }

  leagues?.forEach((league:any) => {
    const leaguesRanking: ClubOption[] = getClubOptionsByLeagueAll(leaguesHistoryClub[league?.id], rankingData);
    if (leaguesRanking.length == 0) {
      series.push({ name: league.name + ' ' + t('AVERAGE'), data: [0, 0, 0, 0, 0] });
      colors.push(colorsList[league.name]);
    } else {
      const metricSums:any = {};
      for (const filter of filters) {
        metricSums[filter] = 0;
      }
      const averageRanking = calculateMetricAverages(leaguesRanking, metricSums);
      const data: number[] = [];
      for (const filter of filters) {
        data.push(averageRanking[filter]);
      }
      series.push({ name: league?.name + ' ' + t('AVERAGE'), data: data });
      colors.push(colorsList[league?.name]);
    }
  });
  ratings?.forEach((rating:any) => {
    const ratingsRanking = getClubOptionsByRating(rating.id, rankingData);
    if (ratingsRanking.length == 0) {
      series.push({ name: rating.name + ' ' + t('AVERAGE'), data: [0, 0, 0, 0, 0] });
      colors.push(colorsList[rating.name]);
    } else {
      const metricSums:any = {};
      for (const filter of filters) {
        metricSums[filter] = 0;
      }
      const averageRanking = calculateMetricAverages(ratingsRanking, metricSums);
      const data: number[] = [];
      for (const filter of filters) {
        data.push(averageRanking[filter]);
      }
      series.push({ name: rating?.name + ' ' + t('AVERAGE'), data: data });
      colors.push(colorsList[rating.name]);
    }
  });
  if (!clubValue && leagues !== undefined && Object.keys(leagues)?.length === 0 && ratings !== undefined && Object.keys(ratings)?.length === 0) {
    const metricSums:any = {};
    for (const filter of filters) {
      metricSums[filter] = 0;
    }
    const averageRanking = rankingData && calculateMetricAverages(rankingData, metricSums);
    const data: number[] = [];
    for (const filter of filters) {
      data.push(averageRanking[filter]);
    }
    series.push({ name: t('Clubs') + ' ' + t('AVERAGE'), data: data });
    colors.push(colorsList['avg']);
  }
  return { series: series, colors: colors };
};

export const findRankingWithMetrics = (inputData:any, filters: any[]) => {
  // Create a ranking object to store the rankings for each metric
  const rankings:any = {};
  for (const filter of filters) {
    rankings[filter] = {};
  }

  // Sort the input data for each metric and assign rankings
  for (const metric in rankings) {
    const sortedData = inputData.slice().sort((a:any, b:any) => b[metric] - a[metric]); // Sort in descending order based on the metric

    let rank = 1;
    for (let i = 0; i < sortedData.length; i++) {
      if (i > 0 && sortedData[i][metric] < sortedData[i - 1][metric]) {
        rank = i + 1; // Increase rank if the current value is less than the previous
      }
      rankings[metric][sortedData[i].clubId] = rank;
    }
  }

  // Create a result object to store the final output
  const result: any[] = [];

  // Iterate through the input data and populate the result object
  for (const item of inputData) {
    const data:any = {};
    const id = item?.clubId;
    data['clubId'] = id;
    data['leagueId'] = item?.leagueId;
    data['currentRating'] = item.currentRating;
    data['clubRanking'] = {
      leagueId: item?.leagueId,
      currentRating: item.currentRating,
    };
    for (const filter of filters) {
      data['clubRanking'][filter] = rankings[filter][id];
    }
    result.push(data);
  }

  return result;
};

// Function raw club data
export const fetchRawClubData = (
  tab: PAGE,
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  financeResponse: ClubData[],
  allClubs: ClubOption[],
  filteredClubIds:any,
) => {
  const data: any[] = [];
  const filteredData = financeResponse.filter((d:any) => filteredClubIds?.includes(d?.clubId));
  if (isAdmin) {
    if (selectedClub) {
      const clubData = getClubsById(tab, selectedClub?.id, filteredData);
      const clubDataByYear = getClubsByYearTemp(yearValue.id, clubData, filteredClubIds);
      return clubDataByYear;
    } else if (leagues?.length > 0) {
      // const leaguesData: ClubOption[] = getClubsByFilter(leagues, allClubs);
      if (filteredClubIds.length > 0) {
        filteredClubIds.map((item:any) => {
          const clubData = getClubsById(tab, item, filteredData);
          const clubDataByYear = getClubsByYearTemp(yearValue?.id, clubData, filteredClubIds);
          data.push(...clubDataByYear);
        });
      }
    } else if (ratings.length > 0) {
      const ratingsData: ClubOption[] = getClubsByFilter(ratings, allClubs);
      if (ratingsData.length > 0) {
        ratingsData.map(item => {
          const clubData = getClubsById(tab, item?.id, filteredData);
          const clubDataByYear = getClubsByYearTemp(yearValue?.id, clubData, filteredClubIds);
          data.push(...clubDataByYear);
        });
      }
    } else {
      allClubs.map(item => {
        const clubData = getClubsById(tab, item?.id, filteredData);
        const clubDataByYear = getClubsByYearTemp(yearValue?.id, clubData, filteredClubIds);
        data.push(...clubDataByYear);
      });
    }
  } else {
    const clubData = activeClubId ? getClubsById(tab, activeClubId, filteredData) : filteredData;
    const clubDataByYear = getClubsByYearTemp(yearValue?.id, clubData, filteredClubIds);
    data.push(...clubDataByYear);
  }

  return data;
};

// Function raw club data
export const filterFacility = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  facilityResponse: any,
) => {
  facilityResponse = getClubsByYearTemp(yearValue?.name ?? '', facilityResponse, filteredClubIds).sort((a:any, b:any) =>
    a.club.name.localeCompare(b.club.name),
  );
  if (selectedClub) {
    const clubData = getClubsfromFacility(selectedClub?.id, facilityResponse);
    return clubData;
  } else if (leagues?.length > 0) {
    return facilityResponse;
  } else if (ratings.length > 0) {
    return getFacilityfromFilter(ratings, facilityResponse);
  } else {
    return facilityResponse;
  }
};

export const calculateStaffFilteredEntries = (
  yearValue: string | undefined,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  allClub: any[] = [],
) => {
  let filteredClubs = getClubsByYearTemp(yearValue ?? '', allClub, filteredClubIds);
  if (selectedClub) {
    filteredClubs = getClubsById(PAGE.STAFF, selectedClub?.id, filteredClubs);
  } else if (leagues?.length > 0 || ratings.length > 0) {
    filteredClubs =
      leagues?.length > 0
        ? filteredClubs
        : getClubsByFilter(
            ratings,
            filteredClubs,
            obj => obj.club,
            club => club?.league.id,
            club => club?.currentRating,
          );
  }
  return filteredClubs.sort((a:any, b:any) => a.club.name.localeCompare(b.club.name));
};
export const calculateFacilityFilteredEntries = (
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  allClub: any[] = [],
) => {
  let filteredClubs = allClub;

  if (selectedClub) {
    filteredClubs = getClubsById(PAGE.FACILITIES, selectedClub?.id, allClub);
  } else if (leagues?.length > 0 || ratings.length > 0) {
    filteredClubs =
      leagues?.length > 0
        ? getClubsByFilter(
            leagues,
            allClub,
            obj => obj.club,
            club => club?.leagueId || club?.league?.id,
            club => club?.currentRating,
          )
        : getClubsByFilter(
            ratings,
            allClub,
            obj => obj.club,
            club => club?.league?.id,
            club => club?.currentRating,
          );
  }
  return filteredClubs;
};
export const calculateFinanceFilteredEntries = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  filteredClubIds: [] | any,
  financeResponse: any[] = [],
) => {
  const filteredData = financeResponse?.filter(
    data => data.year == yearValue.id && filteredClubIds?.includes(data.clubId),
  );
  if (selectedClub) {
    return getClubsById(PAGE.FINANCE, selectedClub?.id, filteredData);
  } else if (leagues?.length > 0) {
    return getClubsByFilter(
      leagues,
      filteredData,
      obj => obj.club,
      obj => obj.leagueId,
      obj => obj.currentRating,
    );
  } else if (ratings.length > 0) {
    return getClubsByFilter(
      ratings,
      filteredData,
      obj => obj.club,
      obj => obj.leagueId,
      obj => obj.currentRating,
    );
  } else {
    return filteredData;
  }
};

export const calculateTeamsFilteredEntries = (
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  allClub: any[] = [],
) => {
  let filteredClubs = allClub;

  if (selectedClub) {
    filteredClubs = getClubsById(PAGE.TEAMS, selectedClub?.id, allClub);
  } else if (leagues?.length > 0 || ratings.length > 0) {
    filteredClubs =
      leagues?.length > 0
        ? getClubsByFilter(
            leagues,
            allClub,
            obj => obj.club,
            club => club?.leagueId || club?.league?.id,
            club => club?.currentRating,
          )
        : getClubsByFilter(
            ratings,
            allClub,
            obj => obj.club,
            club => club?.league?.id,
            club => club?.currentRating,
          );
  }
  return filteredClubs;
};

export const calculateSupportFundData = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  supportfundResponse: any[] = [],
) => {
  const result = new Map<string, { contribution: number; details: any[] }>();
  const filteredResponse = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    supportfundResponse,
  );
  filteredResponse.map((value:any, index) => {
    const entry = result.get(value.clubId);
    if (entry) {
      entry.contribution = entry.contribution + 1;
      entry.details.push(value);
    } else {
      result.set(value.clubId, { contribution: 1, details: [value] });
    }
  });
  return result;
};

// Staff Calculation
export const calculateTotalStaff = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  allStaff: any[] = [],
) => {
  const filteredStaff = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allStaff,
  );

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(filteredStaff.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size == 0 ? 1 : uniqueClubIds.size;

  return selectedClub ? filteredStaff?.length : Math.ceil(filteredStaff?.length / distinctClubCount);
};
export const calculateStaffCost = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  filteredClubIds: [],
  allFinanceData: any[] = [],
) => {
  const filteredFinance = calculateFinanceFilteredEntries(
    yearValue,
    leagues,
    selectedClub,
    ratings,
    filteredClubIds,
    allFinanceData,
  );
  return calculateByTab(
    PAGE.FINANCE,
    filteredFinance as ClubData[],
    yearValue?.id,
    OPTIONSENUM.LABOURCOST,
    '',
    ACTUALBUDGET.ACTUAL,
    true,
    filteredClubIds,
  );
};
export const calculateStaffwithZeroSalary = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  allStaff: any[] = [],
) => {
  const filteredStaff: any = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allStaff,
  );
  const zeroSalary = filteredStaff.filter((staff:any) => staff?.annualSalary == 0);

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(filteredStaff.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size == 0 ? 1 : uniqueClubIds.size;

  return zeroSalary.length / distinctClubCount;
};
export const calculateStaffQualifiedCoaches = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  allStaff: any[] = [],
) => {
  const filteredStaff: any = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allStaff,
  );
  const qualifiedCoachCount = filteredStaff.filter(
    (staff:any) =>
      staff &&
      staff.qualifications != '' &&
      staff.qualifications?.length > 0 && // Check if staff is not null
      !(
        staff.qualifications?.length === 1 &&
        (staff.qualifications[0] === 'DC_Q_QUALIFICATION28' || staff.qualifications[0] === 'DC_Q_QUALIFICATION29')
      ) &&
      !(
        staff.qualifications?.length === 2 &&
        staff.qualifications.includes('DC_Q_QUALIFICATION28') &&
        staff.qualifications.includes('DC_Q_QUALIFICATION29')
      ),
  );

  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(filteredStaff.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size == 0 ? 1 : uniqueClubIds.size;

  return selectedClub ? qualifiedCoachCount?.length : Math.ceil(qualifiedCoachCount?.length / distinctClubCount);
};

// Player Calculation
export const calculateTotalPlayer = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds:any,
  allPlayer: any[] = [],
) => {
  const filteredPlayers = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allPlayer,
  );

  const currentDate = new Date(); // Get the current date
  const pastDateThreshold = new Date(); // Create a threshold date 5 years ago
  pastDateThreshold.setFullYear(currentDate.getFullYear() - 5);

  let count = 0;
  filteredPlayers.forEach((player:any) => {
    // Check if player has clubCareers array and it's not empty
    if (player.clubCareers && player.clubCareers.length > 0) {
      // Iterate over each club career entry
      let loop = true;
      player.clubCareers.forEach((career:any) => {
        // Check if dateFrom is not null or undefined
        if (career.dateFrom && loop) {
          const dateFrom = new Date(career.dateFrom);
          // Check if dateFrom is within the past 5 years
          if (dateFrom >= pastDateThreshold) {
            count++;
            loop = false;
            // Exit loop once a relevant dateFrom is found
            return;
          }
        }
      });
    }
  });
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(filteredPlayers.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size == 0 ? 1 : uniqueClubIds.size;

  return selectedClub ? count : Math.ceil(count / distinctClubCount);
};

export const calculateYearDifference = (dateFrom: string, dateTo: string): number => {
  const fromDate = dateFrom != null ? new Date(dateFrom) : new Date(); // Convert dateFrom string to Date object
  const toDate = dateTo != null ? new Date(dateTo) : new Date(); // Convert dateTo string to Date object

  // Calculate the difference in years
  let yearDifference = toDate.getFullYear() - fromDate.getFullYear();

  // Check if the toDate is earlier in the year than the fromDate
  // If so, decrement the year difference by 1
  // if (
  //   toDate.getMonth() < fromDate.getMonth() ||
  //   (toDate.getMonth() === fromDate.getMonth() && toDate.getDate() < fromDate.getDate())
  // ) {
  //   yearDifference--;
  // }

  return yearDifference;
};

export const getAverageTimeSpentAcademy = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds:any,
  allPlayer: any[] = [],
) => {
  const filteredPlayers = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allPlayer,
  );

  let timeSpent = 0;
  filteredPlayers.forEach((player:any) => {
    // Check if player has clubCareers array and it's not empty
    if (player.academyPeriods && player.academyPeriods.length > 0) {
      // Iterate over each club career entry
      player.academyPeriods.forEach((periods:any) => {
        // Check if dateFrom is not null or undefined
        timeSpent = timeSpent + calculateYearDifference(periods.dateFrom, periods.dateTo);
      });
    }
  });

  return timeSpent > 0 ? timeSpent / filteredPlayers.length : 0;
};

export const calculateInterPlayerCount = (
  yearValue: any,
  leagues: SingleOption[],
  selectedClub: any,
  ratings: SingleValueOption[],
  actualBudget: ACTUALBUDGET,
  filteredClubIds: [],
  allPlayer: any[] = [],
) => {
  const filteredPlayers = calculateStaffFilteredEntries(
    yearValue?.name,
    leagues,
    selectedClub,
    ratings,
    actualBudget,
    filteredClubIds,
    allPlayer,
  );

  const currentDate = new Date(); // Get the current date
  const pastDateThreshold = new Date(); // Create a threshold date 5 years ago
  pastDateThreshold.setFullYear(currentDate.getFullYear() - 5);

  let count = 0;
  filteredPlayers.forEach((player:any) => {
    // Check if player has clubCareers array and it's not empty
    if (player.internationalCareers && player.internationalCareers.length > 0) {
      // Iterate over each club career entry
      player.internationalCareers.forEach((item:any) => {
        // Check if dateFrom is not null or undefined
        if (item.ageGroup == AgeGroup.AG_U18) {
          const dateFrom = new Date(item.dateFrom);
          // Check if dateFrom is within the past 5 years
          if (dateFrom >= pastDateThreshold) {
            count++;
            // Exit loop once a relevant dateFrom is found
            return;
          }
        }
      });
    }
  });
  // Extract unique clubids using a Set
  const uniqueClubIds = new Set(filteredPlayers.map((item:any) => item.clubId));

  // Get the count of distinct clubids
  const distinctClubCount = uniqueClubIds.size == 0 ? 1 : uniqueClubIds.size;

  return selectedClub ? count : Math.ceil(count / distinctClubCount);
};
